import React, { useRef, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";

import EventApi from "../../services/event";
import usePersonData from "../../hooks/usePersonData";
import { config } from "../../config";
import {
    getTagConfig,
    clampQrBounds,
    SECOND_QR_FIXED_VALUE,
} from "../../utils/tagConfig";
import { getFirstAndLastNames, formatDateToTimeZoneUTC } from "../../utils/Functions";
import { eventRoles } from "../../utils/Enums";
import TagEditor from "../../components/TagEditor";

import {
    PageWrapper,
    Header,
    EventName,
    HeaderActions,
    CountersRow,
    CounterCard,
    MainContent,
    Section,
    SectionHeader,
    SectionBody,
    SearchBox,
    ResultsList,
    PersonCard,
    PersonActions,
    IconBtn,
    EmptyState,
    FormGrid,
    FieldGroup,
    PrimaryBtn,
    SecondaryBtn,
    ModalOverlay,
    ModalBox,
    ModalHeader,
    ModalBody,
    ModalFooter,
    TagPreviewBox,
    LoadingOverlay,
    RightSection,
} from "./styles";

/* ─── Inline icons (Unicode) ─── */
const Icon = ({ children, style }) => (
    <span style={{ display: "inline-flex", alignItems: "center", ...style }}>
        {children}
    </span>
);

/* ─── Tag preview (hidden + visible) ─── */
const TagLayout = React.forwardRef(({ person, tagConfig }, ref) => {
    const name = person?.name_tag
        ? person.name_tag
        : getFirstAndLastNames(person?.name || "");

    const qrSide = tagConfig.qrSide || "left";
    const secondQrSide = tagConfig.secondQrSide || "right";
    const textAreaLeft = tagConfig.textAreaLeft ?? 24;
    const textAreaTop = tagConfig.textAreaTop ?? 14;
    const textAreaSpacing = tagConfig.textAreaSpacing ?? 1;
    const itemOrder = Array.isArray(tagConfig.itemOrder)
        ? tagConfig.itemOrder
        : ["role", "name", "company"];
    const nameUppercase = tagConfig.nameUppercase !== false;
    const nameItalic = tagConfig.nameItalic === true;
    const companyUppercase = tagConfig.companyUppercase === true;
    const companyItalic = tagConfig.companyItalic === true;
    const roleUppercase = tagConfig.roleUppercase === true;
    const roleItalic = tagConfig.roleItalic === true;

    const qr1Bounds = clampQrBounds(
        tagConfig.widthMm,
        tagConfig.heightMm,
        tagConfig.qrCodeSizeMm || 25,
        tagConfig.qrCodePosition,
        qrSide
    );

    const qr2Bounds = clampQrBounds(
        tagConfig.widthMm,
        tagConfig.heightMm,
        tagConfig.secondQrCodeSizeMm || 20,
        tagConfig.secondQrCodePosition,
        secondQrSide
    );

    const computedRightMarginMm = (() => {
        let rightMm = 2;
        if (tagConfig.showQr && qrSide === "right") {
            rightMm = Math.max(rightMm, qr1Bounds.sizeMm + qr1Bounds.offsetMm + 2);
        }
        if (tagConfig.showSecondQr && secondQrSide === "right") {
            rightMm = Math.max(rightMm, qr2Bounds.sizeMm + qr2Bounds.offsetMm + 2);
        }
        return Math.min(rightMm, Math.max(2, Number(tagConfig.widthMm) - 10));
    })();

    const computedLeftMarginMm = (() => {
        let leftMm = Math.max(0, Number(textAreaLeft));
        if (tagConfig.showSecondQr && secondQrSide === "left") {
            leftMm = Math.max(leftMm, qr2Bounds.leftMm + qr2Bounds.sizeMm + 2);
        }
        return Math.min(leftMm, Math.max(0, Number(tagConfig.widthMm) - computedRightMarginMm - 10));
    })();

    const computedTopMarginMm = Math.max(
        0,
        Math.min(Number(textAreaTop), Math.max(0, Number(tagConfig.heightMm) - 5))
    );

    return (
        <div
            ref={ref}
            className="printable-tag"
            style={{
                width: `${tagConfig.widthMm}mm`,
                height: `${tagConfig.heightMm}mm`,
                position: "relative",
                boxSizing: "border-box",
                overflow: "hidden",
                backgroundColor: "#ffffff",
            }}
        >
            {/* QR Code — posição absoluta configurável respeitando limites físicos */}
            {tagConfig.showQr && person?.id && (
                <div
                    style={{
                        position: "absolute",
                        top: `${qr1Bounds.topMm}mm`,
                        left: `${qr1Bounds.leftMm}mm`,
                        width: `${qr1Bounds.sizeMm}mm`,
                        height: `${qr1Bounds.sizeMm}mm`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img
                        src={`${config.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=${encodeURIComponent(person.id)}`}
                        alt="QR Code"
                        style={{ width: "100%", height: "100%" }}
                    />
                </div>
            )}

            {/* 2º QR Code Opcional (#anm2026) — respeitando limites físicos e de impressão */}
            {tagConfig.showSecondQr && (
                <div
                    style={{
                        position: "absolute",
                        top: `${qr2Bounds.topMm}mm`,
                        left: `${qr2Bounds.leftMm}mm`,
                        width: `${qr2Bounds.sizeMm}mm`,
                        height: `${qr2Bounds.sizeMm}mm`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img
                        src={`${config.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=${encodeURIComponent(SECOND_QR_FIXED_VALUE)}`}
                        alt={`QR Code ${SECOND_QR_FIXED_VALUE}`}
                        style={{ width: "100%", height: "100%" }}
                    />
                </div>
            )}

            {/* Coluna de texto — flex vertical com ordem configurável */}
            <div
                style={{
                    position: "absolute",
                    top: `${computedTopMarginMm}mm`,
                    left: `${computedLeftMarginMm}mm`,
                    right: `${computedRightMarginMm}mm`,
                    maxHeight: `${Math.max(0, tagConfig.heightMm - computedTopMarginMm)}mm`,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {itemOrder.map((field) => {
                    if (field === "name" && tagConfig.showName && name) {
                        return (
                            <div
                                key="name"
                                style={{
                                    fontSize: `${tagConfig.nameFontSize || 15}pt`,
                                    fontWeight: "700",
                                    fontStyle: nameItalic ? "italic" : "normal",
                                    fontFamily: "'Inter', 'Roboto', sans-serif",
                                    color: "#000000",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    lineHeight: "1.2",
                                    marginBottom: `${textAreaSpacing * 0.4}mm`,
                                    textTransform: nameUppercase ? "uppercase" : "none",
                                }}
                            >
                                {name}
                            </div>
                        );
                    }
                    if (field === "company" && tagConfig.showCompany && person?.company) {
                        return (
                            <div
                                key="company"
                                style={{
                                    fontSize: `${tagConfig.companyFontSize || 14}pt`,
                                    fontWeight: "600",
                                    fontStyle: companyItalic ? "italic" : "normal",
                                    fontFamily: "'Inter', 'Roboto', sans-serif",
                                    color: "#37474f",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: "1.2",
                                    marginBottom: `${textAreaSpacing * 0.4}mm`,
                                    textTransform: companyUppercase ? "uppercase" : "none",
                                }}
                            >
                                {person.company}
                            </div>
                        );
                    }
                    if (field === "role" && tagConfig.showRole && person?.role) {
                        return (
                            <div
                                key="role"
                                style={{
                                    fontSize: `${tagConfig.roleFontSize || 15}pt`,
                                    fontStyle: roleItalic ? "italic" : "normal",
                                    fontWeight: "600",
                                    fontFamily: "'Inter', 'Roboto', sans-serif",
                                    color: "#546e7a",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    lineHeight: "1.2",
                                    marginBottom: `${textAreaSpacing * 0.4}mm`,
                                    textTransform: roleUppercase ? "uppercase" : "none",
                                }}
                            >
                                {person.role}
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
});

/* ─── Blank new person form ─── */
const blankPerson = () => ({
    name: "",
    name_tag: "",
    email: "",
    cpf: "",
    company: "",
    role: "",
    phone: "",
    event_category: "",
    certificate: 0,
    enable: true,
    vip: false,
    status_id: 1,
    solicitation_approved: false,
    is_guest: false,
    gender: "",
    custom_fields: "",
    interage_id: "",
});

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Credentials = (props) => {
    const eventId =
        props?.computedMatch?.params?.event_id ||
        props?.match?.params?.event_id ||
        1;

    const { updateCredential } = usePersonData();

    /* ── tag config ── */
    const [configVersion, setConfigVersion] = useState(0);
    const tagConfig = React.useMemo(
        () => getTagConfig(eventId),
        [eventId, configVersion]
    );

    /* ── refs ── */
    const printRef = useRef(null);
    const searchInputRef = useRef(null);
    const debounceRef = useRef(null);

    /* ── state: search ── */
    const [searchTerm, setSearchTerm] = useState("");
    const [allPeople, setAllPeople] = useState([]);       // lista completa carregada ao abrir
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true); // carregamento inicial

    /* ── state: counters ── */
    const [totalPeople, setTotalPeople] = useState(null);
    const [totalCredentialed, setTotalCredentialed] = useState(null);

    /* ── state: selected person (for print) ── */
    const [selectedPerson, setSelectedPerson] = useState(null);

    /* ── state: edit modal ── */
    const [editPerson, setEditPerson] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    /* ── state: new form ── */
    const [newPerson, setNewPerson] = useState(blankPerson());
    const [isCreating, setIsCreating] = useState(false);

    /* ── state: print confirm modal ── */
    const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
    const [pendingPrintPerson, setPendingPrintPerson] = useState(null);

    /* ── state: tag settings ── */
    const [settingsOpen, setSettingsOpen] = useState(false);

    /* ── state: general loading ── */
    const [isLoading, setIsLoading] = useState(false);

    /* ─────────────────────────────────────────
       PRINT SETUP
    ───────────────────────────────────────── */
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `
            @page {
                size: ${tagConfig.widthMm}mm ${tagConfig.heightMm}mm;
                margin: 0;
            }
            body {
                margin: 0;
                background: #fff;
                -webkit-print-color-adjust: exact;
            }
            @media print {
                .printable-tag {
                    border: none !important;
                    box-shadow: none !important;
                    background: white !important;
                    margin: 0 !important;
                }
            }
        `,
    });

    /* ─────────────────────────────────────────
       PRÉ-CARREGAMENTO DA LISTA (ao montar)
    ───────────────────────────────────────── */
    useEffect(() => {
        const loadAll = async () => {
            setIsLoadingList(true);
            try {
                // Tenta buscar todos numa página grande
                const resp = await EventApi.getAllPersonByEvent(eventId, {
                    page: 1,
                    perPage: 500,
                });
                const data = resp?.data?.data ?? (Array.isArray(resp?.data) ? resp.data : []);
                const total = resp?.data?.total ?? data.length;
                setAllPeople(data);
                setTotalPeople(total ?? null);
                // Mostra todos inicialmente na lista
                setSearchResults(data);
                setHasSearched(true);
            } catch (e) {
                // silently ignore
            } finally {
                setIsLoadingList(false);
            }
        };
        loadAll();
    }, [eventId]);

    /* ─────────────────────────────────────────
       SEARCH — filtra localmente na lista
    ───────────────────────────────────────── */
    const doSearch = useCallback(
        (term) => {
            if (!term || term.trim().length < 1) {
                // Sem filtro: mostra todos
                setSearchResults(allPeople);
                setHasSearched(true);
                return;
            }
            setHasSearched(true);
            const cleaned = term.replace(/\D/g, "");
            const isDoc = cleaned.length >= 6 && /^\d+$/.test(cleaned);
            const lc = term.trim().toLowerCase();
            const filtered = allPeople.filter((p) => {
                if (isDoc) {
                    return (p.cpf || "").replace(/\D/g, "").includes(cleaned);
                }
                return (
                    (p.name || "").toLowerCase().includes(lc) ||
                    (p.company || "").toLowerCase().includes(lc) ||
                    (p.role || "").toLowerCase().includes(lc)
                );
            });
            setSearchResults(filtered);
        },
        [allPeople]
    );

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            doSearch(val);
        }, 150); // debounce curto pois é local
    };

    /* ─────────────────────────────────────────
       PRINT FLOW
    ───────────────────────────────────────── */
    const triggerPrint = async (person) => {
        setSelectedPerson(person);
        setIsLoading(true);
        try {
            // Register credential on backend first
            await updateCredential(person.id, eventId);
        } catch (e) {
            console.error("Erro ao registrar credencial:", e);
        } finally {
            setIsLoading(false);
        }
        // Small delay to ensure DOM update with correct person data
        setTimeout(() => {
            handlePrint();
        }, 100);
    };

    /* ─────────────────────────────────────────
       EDIT PERSON
    ───────────────────────────────────────── */
    const openEdit = (person) => {
        setEditPerson({ ...person, name_tag: person.name_tag || person.name });
        setIsEditOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditPerson((prev) => ({ ...prev, [name]: value }));
    };

    const saveEdit = async () => {
        if (!editPerson?.name || editPerson.name.length < 2) {
            toast.warn("Insira o nome completo");
            return;
        }
        setIsSaving(true);
        try {
            const resp = await EventApi.updatePerson(editPerson.id, {
                ...editPerson,
                cpf: editPerson.cpf ? editPerson.cpf.replace(/\D/g, "") : null,
                phone: editPerson.phone ? editPerson.phone.replace(/\D/g, "") : null,
                email: editPerson.email ? editPerson.email.toLowerCase() : "",
                company: editPerson.company ? editPerson.company.toUpperCase() : "",
                event_days_subscribed: editPerson.event_days_subscribed || ["*"],
                event_days_attended: editPerson.event_days_attended || ["*"],
            });
            if (resp?.data) {
                toast.success("Participante atualizado!");
                setIsEditOpen(false);

                // Atualiza a lista local para refletir as mudanças imediatamente
                const updated = { ...editPerson };
                setAllPeople((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
                setSearchResults((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            } else {
                toast.error("Erro ao salvar. Tente novamente.");
            }
        } catch (e) {
            toast.error("Erro ao salvar");
        } finally {
            setIsSaving(false);
        }
    };

    /* ─────────────────────────────────────────
       CREATE NEW PERSON
    ───────────────────────────────────────── */
    const handleNewChange = (e) => {
        const { name, value } = e.target;
        setNewPerson((prev) => ({ ...prev, [name]: value }));
    };

    const createPerson = async () => {
        if (!newPerson.name || newPerson.name.trim().length < 2) {
            toast.warn("Insira o nome completo");
            return;
        }

        if (!newPerson.phone || newPerson.phone.replace(/\D/g, "").length < 8) {
            toast.warn("Insira o telefone corretamente");
            return;
        }

        if (!newPerson.email || newPerson.email.trim().length < 3) {
            toast.warn("Insira o email corretamente");
            return;
        }

        setIsCreating(true);
        try {
            const resp = await EventApi.createPerson({
                ...newPerson,
                cpf: newPerson.cpf ? newPerson.cpf.replace(/\D/g, "") : null,
                phone: newPerson.phone ? newPerson.phone.replace(/\D/g, "") : null,
                email: newPerson.email ? newPerson.email.toLowerCase() : "",
                company: newPerson.company ? newPerson.company.toUpperCase() : "",
                event_id: Number(eventId),
                grade_id: 1,
                accepted_terms: true,
                allow_marketing: true,
            });
            if (resp?.data) {
                const createdPerson = resp.data?.data || resp.data;
                toast.success("Participante inscrito com sucesso!");

                // Update counters
                setTotalPeople((prev) => (prev !== null ? prev + 1 : null));

                // Populate form name_tag if empty
                const personForPrint = {
                    ...newPerson,
                    id: createdPerson?.id || createdPerson,
                    name_tag: newPerson.name_tag || getFirstAndLastNames(newPerson.name),
                };

                // Adiciona à lista local para aparecer imediatamente na busca
                const fullPerson = { ...newPerson, id: createdPerson?.id || createdPerson };
                setAllPeople((prev) => [fullPerson, ...prev]);
                setSearchResults((prev) => [fullPerson, ...prev]);

                // Ask if wants to print
                setPendingPrintPerson(personForPrint);
                setPrintConfirmOpen(true);

                // Reset form
                setNewPerson(blankPerson());
            } else if (resp?.response?.data?.message) {
                const msg = resp.response.data.message;
                if (msg.includes("cpf")) toast.warn("CPF já cadastrado");
                else if (msg.includes("email")) toast.warn("Email já cadastrado");
                else toast.warn("Não foi possível criar o participante");
            } else {
                toast.warn("Não foi possível criar o participante");
            }
        } catch (e) {
            toast.error("Erro ao criar participante");
        } finally {
            setIsCreating(false);
        }
    };

    /* ─────────────────────────────────────────
       PRINT CONFIRM (after creation)
    ───────────────────────────────────────── */
    const confirmPrintNewPerson = async () => {
        setPrintConfirmOpen(false);
        if (!pendingPrintPerson) return;
        await triggerPrint(pendingPrintPerson);
        setPendingPrintPerson(null);
    };

    const skipPrint = () => {
        setPrintConfirmOpen(false);
        setPendingPrintPerson(null);
    };

    /* ─────────────────────────────────────────
       RENDER
    ───────────────────────────────────────── */
    return (
        <PageWrapper>
            {/* ── HIDDEN PRINT AREA ── */}
            <div style={{ display: "none" }}>
                <TagLayout ref={printRef} person={selectedPerson || {}} tagConfig={tagConfig} />
            </div>

            {/* ── LOADING ── */}
            {isLoading && (
                <LoadingOverlay>
                    <div className="spinner" />
                </LoadingOverlay>
            )}

            {/* ── HEADER ── */}
            <Header>
                <EventName>{config.EVENT_NAME || "Credenciamento"}</EventName>
                <HeaderActions>
                    <button
                        onClick={() => setSettingsOpen(true)}
                        title="Configurar Etiqueta"
                        style={{
                            background: "rgba(255,255,255,0.15)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: 8,
                            color: "#fff",
                            padding: "7px 14px",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            transition: "background 0.2s",
                        }}
                    >
                        ⚙️ Configurar Etiqueta
                    </button>
                </HeaderActions>
            </Header>

            {/* ── COUNTERS ── */}
            <CountersRow>
                <CounterCard color="#175d90">
                    <span className="count-number">
                        {totalPeople !== null ? totalPeople : "—"}
                    </span>
                    <span className="count-label">Inscritos</span>
                </CounterCard>
                <CounterCard color="#0a9e9e">
                    <span className="count-number">
                        {searchResults.length > 0 ? searchResults.length : "—"}
                    </span>
                    <span className="count-label">Encontrados</span>
                </CounterCard>
                <CounterCard color="#733bb9">
                    <span className="count-number">
                        {searchResults.filter((p) => p.credential_generated).length > 0
                            ? searchResults.filter((p) => p.credential_generated).length
                            : "—"}
                    </span>
                    <span className="count-label">Credenciados</span>
                </CounterCard>
            </CountersRow>

            {/* ── MAIN CONTENT ── */}
            <MainContent>
                {/* ── LEFT: SEARCH & RESULTS ── */}
                <Section>
                    <SectionHeader>
                        🔍 Buscar Participante
                    </SectionHeader>
                    <SectionBody>
                        <SearchBox>
                            <span className="search-icon">🔍</span>
                            <input
                                ref={searchInputRef}
                                id="credenciamento-search"
                                type="text"
                                placeholder="Digite nome ou documento (CPF/Passaporte)…"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                autoComplete="off"
                                autoFocus
                            />
                        </SearchBox>

                        {isLoadingList && (
                            <div style={{ textAlign: "center", padding: "20px", color: "#888" }}>
                                ⏳ Carregando participantes…
                            </div>
                        )}

                        {!isLoadingList && hasSearched && searchResults.length === 0 && (
                            <EmptyState>
                                <div className="empty-icon">🙅</div>
                                <div>Nenhum participante encontrado</div>
                                <div style={{ fontSize: 12, marginTop: 4, color: "#bbb" }}>
                                    Verifique o nome/documento ou cadastre abaixo
                                </div>
                            </EmptyState>
                        )}

                        {!isLoadingList && searchTerm.trim().length > 0 && searchResults.length > 0 && (
                            <div style={{ fontSize: 12, color: "#888", padding: "4px 0 6px 2px" }}>
                                {searchResults.length} resultado{searchResults.length !== 1 ? "s" : ""} para "{searchTerm}"
                            </div>
                        )}

                        <ResultsList>
                            {searchResults.map((p) => (
                                <PersonCard
                                    key={p.id}
                                    credentialed={p.credential_generated ? 1 : 0}
                                >
                                    <div className="person-info">
                                        <div className="person-name">{p.name}</div>
                                        <div className="person-sub">
                                            {[p.company, p.cpf].filter(Boolean).join(" · ")}
                                        </div>
                                    </div>
                                    <span className="person-badge">
                                        {p.credential_generated ? "✓ Credenciado" : "Pendente"}
                                    </span>
                                    <PersonActions>
                                        <IconBtn
                                            variant="default"
                                            title="Editar"
                                            onClick={() => openEdit(p)}
                                        >
                                            ✏️
                                        </IconBtn>
                                        <IconBtn
                                            variant="primary"
                                            title="Imprimir Credencial"
                                            onClick={() => triggerPrint(p)}
                                        >
                                            🖨️
                                        </IconBtn>
                                    </PersonActions>
                                </PersonCard>
                            ))}
                        </ResultsList>
                    </SectionBody>
                </Section>

                {/* ── RIGHT: NEW PERSON FORM ── */}
                <RightSection>
                    <SectionHeader>
                        ➕ Inscrição no Local
                    </SectionHeader>
                    <SectionBody>
                        <FormGrid>
                            <div className="full-width">
                                <FieldGroup>
                                    <label>Nome Completo *</label>
                                    <input
                                        id="new-name"
                                        name="name"
                                        type="text"
                                        placeholder="Nome completo"
                                        value={newPerson.name}
                                        onChange={handleNewChange}
                                        autoComplete="off"
                                        required
                                    />
                                </FieldGroup>
                            </div>

                            <div className="full-width">
                                <FieldGroup>
                                    <label>Nome na Etiqueta</label>
                                    <input
                                        id="new-name-tag"
                                        name="name_tag"
                                        type="text"
                                        placeholder={getFirstAndLastNames(newPerson.name) || "Automático"}
                                        value={newPerson.name_tag}
                                        onChange={handleNewChange}
                                        autoComplete="off"
                                    />
                                </FieldGroup>
                            </div>

                            {/* <FieldGroup>
                                <label>CPF / Documento</label>
                                <input
                                    id="new-cpf"
                                    name="cpf"
                                    type="text"
                                    placeholder="000.000.000-00"
                                    value={newPerson.cpf}
                                    onChange={handleNewChange}
                                />
                            </FieldGroup> */}

                            <FieldGroup>
                                <label>Telefone *</label>
                                <input
                                    id="new-phone"
                                    name="phone"
                                    type="text"
                                    placeholder="(00) 00000-0000"
                                    value={newPerson.phone}
                                    onChange={handleNewChange}
                                    required
                                />
                            </FieldGroup>

                            <FieldGroup>
                                <label>Email *</label>
                                <input
                                    id="new-email"
                                    name="email"
                                    type="email"
                                    placeholder="email@exemplo.com"
                                    value={newPerson.email}
                                    onChange={handleNewChange}
                                    required
                                />
                            </FieldGroup>

                            <FieldGroup>
                                <label>Empresa</label>
                                <input
                                    id="new-company"
                                    name="company"
                                    type="text"
                                    placeholder="Empresa"
                                    value={newPerson.company}
                                    onChange={handleNewChange}
                                />
                            </FieldGroup>

                            <FieldGroup>
                                <label>Titulo</label>
                                <input
                                    id="new-role"
                                    name="role"
                                    type="text"
                                    placeholder="Titulo"
                                    value={newPerson.role}
                                    onChange={handleNewChange}
                                />
                            </FieldGroup>

                            {/* <div className="full-width">
                                <FieldGroup>
                                    <label>Categoria</label>
                                    <select
                                        id="new-category"
                                        name="event_category"
                                        value={newPerson.event_category}
                                        onChange={handleNewChange}
                                    >
                                        <option value="">Selecione...</option>
                                        {eventRoles.map((r) => (
                                            <option key={r} value={r}>
                                                {r}
                                            </option>
                                        ))}
                                    </select>
                                </FieldGroup>
                            </div> */}
                        </FormGrid>

                        {/* Live tag preview */}
                        {newPerson.name.trim().length > 1 && (
                            <div style={{ marginTop: 16 }}>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: "#888",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        marginBottom: 4,
                                        textAlign: "center",
                                    }}
                                >
                                    Preview da etiqueta
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        overflow: "auto",
                                        border: "1px dashed #ddd",
                                        borderRadius: 8,
                                        padding: 8,
                                        background: "#f8f8f8",
                                    }}
                                >
                                    <TagLayout
                                        person={{
                                            ...newPerson,
                                            id: "preview",
                                            name_tag:
                                                newPerson.name_tag ||
                                                getFirstAndLastNames(newPerson.name),
                                        }}
                                        tagConfig={tagConfig}
                                    />
                                </div>
                            </div>
                        )}

                        <PrimaryBtn
                            id="btn-create-person"
                            onClick={createPerson}
                            disabled={isCreating || !newPerson.name.trim()}
                            style={{ marginTop: 16 }}
                        >
                            {isCreating ? "Inscrevendo…" : "✅ Inscrever Participante"}
                        </PrimaryBtn>
                    </SectionBody>
                </RightSection>
            </MainContent>

            {/* ═══════════════════════════════
                MODAL: Confirmar Impressão após criação
            ═══════════════════════════════ */}
            {printConfirmOpen && (
                <ModalOverlay onClick={skipPrint}>
                    <ModalBox onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <span>🖨️ Imprimir Credencial?</span>
                        </ModalHeader>
                        <ModalBody>
                            <div style={{ fontSize: 15, color: "#333", marginBottom: 8 }}>
                                Participante inscrito com sucesso!
                            </div>
                            <div style={{ fontSize: 14, color: "#555" }}>
                                Deseja imprimir a credencial de{" "}
                                <strong>
                                    {pendingPrintPerson?.name_tag ||
                                        getFirstAndLastNames(pendingPrintPerson?.name || "")}
                                </strong>{" "}
                                agora?
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <SecondaryBtn id="btn-skip-print" onClick={skipPrint}>
                                Agora não
                            </SecondaryBtn>
                            <button
                                id="btn-confirm-print"
                                onClick={confirmPrintNewPerson}
                                style={{
                                    padding: "9px 22px",
                                    background: "linear-gradient(135deg, #175d90 0%, #0a9e9e 100%)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                            >
                                🖨️ Imprimir Agora
                            </button>
                        </ModalFooter>
                    </ModalBox>
                </ModalOverlay>
            )}

            {/* ═══════════════════════════════
                MODAL: Editar Participante
            ═══════════════════════════════ */}
            {isEditOpen && editPerson && (
                <ModalOverlay onClick={() => setIsEditOpen(false)}>
                    <ModalBox size="lg" onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <span>✏️ Editar Participante</span>
                            <button
                                onClick={() => setIsEditOpen(false)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#fff",
                                    fontSize: 20,
                                    cursor: "pointer",
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </button>
                        </ModalHeader>
                        <ModalBody>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "#aaa",
                                    marginBottom: 12,
                                }}
                            >
                                ID: {editPerson.id}
                            </div>
                            <FormGrid>
                                <div className="full-width">
                                    <FieldGroup>
                                        <label>Nome Completo *</label>
                                        <input
                                            id="edit-name"
                                            name="name"
                                            type="text"
                                            value={editPerson.name || ""}
                                            onChange={handleEditChange}
                                        />
                                    </FieldGroup>
                                </div>
                                <FieldGroup>
                                    <label>Nome na Etiqueta</label>
                                    <input
                                        id="edit-name-tag"
                                        name="name_tag"
                                        type="text"
                                        value={editPerson.name_tag || ""}
                                        onChange={handleEditChange}
                                    />
                                </FieldGroup>
                                <FieldGroup>
                                    <label>Telefone</label>
                                    <input
                                        id="edit-phone"
                                        name="phone"
                                        type="text"
                                        placeholder="(00) 00000-0000"
                                        value={editPerson.phone || ""}
                                        onChange={handleEditChange}
                                    />
                                </FieldGroup>
                                <FieldGroup>
                                    <label>CPF / Documento</label>
                                    <input
                                        id="edit-cpf"
                                        name="cpf"
                                        type="text"
                                        value={editPerson.cpf || ""}
                                        onChange={handleEditChange}
                                    />
                                </FieldGroup>
                                <FieldGroup>
                                    <label>Email</label>
                                    <input
                                        id="edit-email"
                                        name="email"
                                        type="email"
                                        value={editPerson.email || ""}
                                        onChange={handleEditChange}
                                    />
                                </FieldGroup>
                                <FieldGroup>
                                    <label>Empresa</label>
                                    <input
                                        id="edit-company"
                                        name="company"
                                        type="text"
                                        value={editPerson.company || ""}
                                        onChange={handleEditChange}
                                    />
                                </FieldGroup>
                                <FieldGroup>
                                    <label>Titulo</label>
                                    <input
                                        id="edit-role"
                                        name="role"
                                        type="text"
                                        value={editPerson.role || ""}
                                        onChange={handleEditChange}
                                    />
                                </FieldGroup>
                                {/* <div className="full-width">
                                    <FieldGroup>
                                        <label>Categoria</label>
                                        <select
                                            id="edit-category"
                                            name="event_category"
                                            value={editPerson.event_category || ""}
                                            onChange={handleEditChange}
                                        >
                                            <option value="">Selecione...</option>
                                            {eventRoles.map((r) => (
                                                <option key={r} value={r}>
                                                    {r}
                                                </option>
                                            ))}
                                        </select>
                                    </FieldGroup>
                                </div> */}
                            </FormGrid>

                            {/* Preview da etiqueta no edit */}
                            <div style={{ marginTop: 16 }}>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: "#888",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        marginBottom: 4,
                                        textAlign: "center",
                                    }}
                                >
                                    Preview da etiqueta
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        overflow: "auto",
                                        border: "1px dashed #ddd",
                                        borderRadius: 8,
                                        padding: 8,
                                        background: "#f8f8f8",
                                    }}
                                >
                                    <TagLayout
                                        person={{
                                            ...editPerson,
                                            name_tag:
                                                editPerson.name_tag ||
                                                getFirstAndLastNames(editPerson.name || ""),
                                        }}
                                        tagConfig={tagConfig}
                                    />
                                </div>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <SecondaryBtn
                                id="btn-edit-cancel"
                                onClick={() => setIsEditOpen(false)}
                            >
                                Cancelar
                            </SecondaryBtn>
                            <button
                                id="btn-edit-print"
                                onClick={() => {
                                    setIsEditOpen(false);
                                    triggerPrint(editPerson);
                                }}
                                style={{
                                    padding: "9px 18px",
                                    background: "#f0f0f0",
                                    color: "#444",
                                    border: "none",
                                    borderRadius: 8,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                🖨️ Imprimir
                            </button>
                            <button
                                id="btn-edit-save"
                                onClick={saveEdit}
                                disabled={isSaving}
                                style={{
                                    padding: "9px 22px",
                                    background: "linear-gradient(135deg, #175d90 0%, #0a9e9e 100%)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    opacity: isSaving ? 0.6 : 1,
                                }}
                            >
                                {isSaving ? "Salvando…" : "💾 Salvar"}
                            </button>
                        </ModalFooter>
                    </ModalBox>
                </ModalOverlay>
            )}

            {/* ═══════════════════════════════
                TAG EDITOR (configuração de etiqueta)
            ═══════════════════════════════ */}
            <TagEditor
                eventId={eventId}
                dialogPrintOpen={settingsOpen}
                handleClosePrint={() => {
                    setSettingsOpen(false);
                    setConfigVersion((prev) => prev + 1);
                }}
            />
        </PageWrapper>
    );
};

export default Credentials;
